import { useState } from 'react';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { SecurityControl, SecurityGroup } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { Button, Input } from '../../components/ui';
import { useUnsavedDraft } from '../../components/ui/CollapsibleGroup';

type Notify = (message: string, tone?: 'info' | 'danger') => void;

/**
 * One config accordion's controls — the toggles, the dropdowns, the text and
 * account fields — as a staged form: edits collect in a draft and one Save writes
 * the whole group. The controls come from the model itself
 * (`GET /devices/:id/security` for the Security tab, `/config/:section` for every
 * other model-aware Configs section, both resolved by the same engine), so this
 * renders whatever the CPE actually exposes and never invents a control the ONU
 * cannot carry. A background refetch cannot clobber an edit in progress — a
 * control shows `draft ?? reported`, so a typed value wins until it is saved or
 * the group is closed.
 *
 * The write target is parametrised: `postPath`/`invalidateKeys` default to the
 * security route, and a generic section passes its own `/config/:section` path
 * and query key, so one staged-form component serves every section.
 */
export function SecurityGroupControls({
  group,
  deviceId,
  onNotice,
  postPath,
  invalidateKeys,
}: {
  group: SecurityGroup;
  deviceId: string;
  onNotice: Notify;
  /** Where Save posts; defaults to the Security route. */
  postPath?: string;
  /** Query keys to invalidate after a successful save. */
  invalidateKeys?: QueryKey[];
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const canWrite = can('device.write');
  const [draft, setDraft] = useState<Record<string, boolean | string>>({});

  const edit = (key: string, value: boolean | string) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // What each control shows now — the edit if there is one, else the CPE value.
  const shown = (c: SecurityControl): boolean | string | null =>
    key(c) in draft ? draft[key(c)]! : c.value;

  // Dirty when a control has an override that differs from what the CPE reports.
  // A password (value null, write-only) is dirty only when something was typed.
  const isDirty = (c: SecurityControl): boolean => {
    if (!(key(c) in draft)) return false;
    const v = draft[key(c)];
    if (c.secret) return typeof v === 'string' && v.length > 0;
    return v !== c.value;
  };
  const dirty = group.controls.filter(isDirty);
  // The Configs / Security panel above keeps this group's draft mounted
  // while another panel is open, rather than dropping it on the fold.
  useUnsavedDraft(dirty.length > 0);
  // A group whose every control is read-only (e.g. the Management panel) shows no
  // Save affordance at all, rather than a permanently-disabled button.
  const hasWritable = group.controls.some((c) => c.writable);
  // A control this model will not write and that carries no note of its own
  // is not shown (operator, 2026-10-21: hide options not supported until we
  // add them); a read-only control WITH a note is a deliberate reading and stays.
  const shownControls = group.controls.filter((c) => c.writable || Boolean(c.note));

  const target = postPath ?? `/devices/${encodeURIComponent(deviceId)}/security`;
  const keysToInvalidate: QueryKey[] = invalidateKeys ?? [
    ['device', deviceId, 'security'],
    ['device', deviceId],
  ];

  const save = useMutation({
    mutationFn: () =>
      api.post(target, {
        values: dirty.map((c) => ({ key: c.key, value: draft[key(c)]! })),
      }),
    onSuccess: async () => {
      onNotice(
        dirty.length === 1
          ? `${dirty[0]!.label} updated.`
          : `${dirty.length} ${group.label.toLowerCase()} settings updated.`,
      );
      setDraft({});
      await Promise.all(
        keysToInvalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not save', 'danger'),
  });

  return (
    <div>
      <div className="divide-y divide-line">
        {shownControls.length === 0 && (
          <p className="px-5 py-3 text-xs text-faint">This model lets nothing in this group be written over TR-069.</p>
        )}
        {shownControls.map((c) => (
          <div key={c.key} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <div className="min-w-0">
              <span className="text-sm text-ink">{c.label}</span>
              {c.note && <p className="mt-0.5 text-xs text-faint">{c.note}</p>}
              {/* A control with its own note explains its own read-only-ness; the
                  generic line is only for a control the model simply won't write. */}
              {!c.writable && !c.note && (
                <p className="mt-0.5 text-xs text-faint">Read-only on this model.</p>
              )}
            </div>
            <div className="shrink-0">
              {c.kind === 'toggle' ? (
                <Segmented
                  value={shown(c) === true}
                  disabled={!canWrite || !c.writable}
                  onChange={(v) => edit(c.key, v)}
                />
              ) : c.kind === 'select' ? (
                <select
                  className="input w-44"
                  disabled={!canWrite || !c.writable}
                  value={typeof shown(c) === 'string' ? (shown(c) as string) : ''}
                  onChange={(e) => edit(c.key, e.target.value)}
                >
                  {(c.options ?? []).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  type={c.kind === 'password' ? 'password' : 'text'}
                  className="w-44"
                  disabled={!canWrite || !c.writable}
                  autoComplete={c.kind === 'password' ? 'new-password' : 'off'}
                  placeholder={c.secret ? 'leave blank to keep' : ''}
                  value={typeof shown(c) === 'string' ? (shown(c) as string) : ''}
                  onChange={(e) => edit(c.key, e.target.value)}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      {canWrite && hasWritable && (
        <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
          {dirty.length > 0 && (
            <Button onClick={() => setDraft({})} disabled={save.isPending}>
              Reset
            </Button>
          )}
          <Button
            variant="primary"
            disabled={dirty.length === 0 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}

function key(c: SecurityControl): string {
  return c.key;
}

/** On/Off segmented control, matching the firewall toggle elsewhere. */
export function Segmented({
  value,
  disabled,
  onChange,
}: {
  value: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-line">
      {(
        [
          ['On', true],
          ['Off', false],
        ] as const
      ).map(([label, v]) => {
        const active = value === v;
        return (
          <button
            key={label}
            type="button"
            disabled={disabled}
            onClick={() => onChange(v)}
            className={`px-4 py-1 text-sm transition ${
              active
                ? v
                  ? 'bg-success text-white'
                  : 'bg-surface-3 text-ink'
                : 'text-muted hover:bg-surface-2'
            } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The firewall for an OMCI ONU (no CPE on this ACS): a simple on/off over the
 * OLT verb, since the rich CPE controls are a TR-069 thing. On ZTE the `/firewall`
 * route drives the OMCI verb; refused honestly on an OMCI-only Huawei.
 */
export function OmciFirewall({
  oltId,
  onuId,
  vendor,
  firewalled,
  onNotice,
}: {
  oltId: string;
  onuId: string;
  vendor: string;
  firewalled: boolean | null;
  onNotice: Notify;
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const supported = vendor === 'zte';

  const setFirewall = useMutation({
    mutationFn: (enabled: boolean) =>
      api.post(`/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/firewall`, {
        enabled,
        dryRun: false,
      }),
    onSuccess: async (_r, enabled) => {
      onNotice(`Firewall turned ${enabled ? 'on' : 'off'}.`);
      await queryClient.invalidateQueries({ queryKey: ['onu', onuId, 'omci-config'] });
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not change the firewall', 'danger'),
  });

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
      <div className="min-w-0">
        <span className="text-sm font-medium text-ink">Firewall</span>
        <p className="mt-0.5 text-xs text-faint">
          {supported
            ? 'Whether the ONU answers unsolicited traffic from the WAN side. Put it on TR-069 for the full per-service controls.'
            : 'Not controllable from here — this ONU is on OMCI and its OLT has no firewall verb. Put it on TR-069 to control it.'}
        </p>
      </div>
      {supported && can('onu.provision') && (
        <Segmented
          value={firewalled === true}
          disabled={setFirewall.isPending}
          onChange={(v) => setFirewall.mutate(v)}
        />
      )}
    </div>
  );
}
